import React from 'react';

import { Box, Button, Grid, Link, Typography } from '@mui/material';
import { omit } from 'es-toolkit';

import { Form } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import { useMiddeskCreateBusinessForVerifierMutation, VerifiersListDocument } from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useVerifierById } from 'src/hooks/verifier';
import { useVerifierStaffList } from 'src/hooks/verifierStaff';
import { SxProp } from 'src/types/mui';
import { formatFeinNumber } from 'src/utils/fein';
import { validateWithYupSchema } from 'src/utils/yup';

import { VerifyAutomaticModalFormFields, VerifyAutomaticModalFormValues } from './types';
import {
  getVerifierStaffListFilterQuery,
  getVerifyAutomaticModalFormInitialValues,
  VerifyAutomaticModalFormValidationSchema,
} from './utils';

export interface VerifierVerifyAutomaticByCVStaffModalFormProps {
  verifierId: string | undefined;
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
}

export const VerifierVerifyAutomaticByCVStaffModalForm: React.FC<
  VerifierVerifyAutomaticByCVStaffModalFormProps
> = ({ verifierId, switchClosability, onCloseModal }) => {
  const { createApolloContext } = useNotification();

  const { verifier } = useVerifierById(verifierId);

  const [createBusinessForVerifierMutation] = useMiddeskCreateBusinessForVerifierMutation({
    refetchQueries: [VerifiersListDocument],
    context: createApolloContext({
      successMessage: 'Verifier has been successfully updated',
    }),
  });

  const { verifierStaffList } = useVerifierStaffList(verifierId, getVerifierStaffListFilterQuery());

  const initialValues: VerifyAutomaticModalFormValues = React.useMemo(
    () => getVerifyAutomaticModalFormInitialValues(verifier, verifierStaffList[0]),
    [verifier, verifierStaffList],
  );

  const handleSubmit = React.useCallback(
    async (values: VerifyAutomaticModalFormValues) => {
      try {
        await createBusinessForVerifierMutation({
          variables: {
            verifierId: verifierId ?? '',
            businessFields: {
              ...omit(values, [VerifyAutomaticModalFormFields.FEIN]),
              fein: values.FEIN,
            },
          },
        });
        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [createBusinessForVerifierMutation, onCloseModal, switchClosability, verifierId],
  );

  return (
    <Form<VerifyAutomaticModalFormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{ valid: true, submitting: true, values: true }}
      validate={validateWithYupSchema(VerifyAutomaticModalFormValidationSchema)}
    >
      {({ submitting, valid, values }) => (
        <React.Fragment>
          <Box>
            <Typography variant="body1" sx={modalTextStyles} mb={3}>
              To start the process of verifying the Verifier on Middesk (
              <Link
                href="https://www.middesk.com/"
                underline="none"
                component="a"
                target="_blank"
                sx={linkStyles}
              >
                https://www.middesk.com/
              </Link>
              ), check Verifier’s company information and click the &quot;Confirm&quot; button. Once
              Middesk completes its verification process, the Verifier&apos;s status will update
              automatically and you will receive an email notification.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <GridLine name="Legal Business Name" value={values.legalBusinessName} />
            <GridLine name="FEIN" value={formatFeinNumber(values.FEIN)} />

            <Grid size={12}>
              <Typography variant="subtitle1" component="p">
                Company Address
              </Typography>
            </Grid>
            <GridLine name="Street" value={values.address?.street} />
            <GridLine name="City" value={values.address?.city} />
            <GridLine name="County" value={values.address?.county} />
            <GridLine name="State" value={values.address?.state} />
            <GridLine name="Zip Code" value={values.address?.zipCode} />

            <Grid size={12}>
              <Typography variant="subtitle1" component="p">
                Contact Information
              </Typography>
            </Grid>
            <GridLine name="Contact Phone" value={formatPhoneNumber(values.contactPhone)} />
            <GridLine name="Website URL" value={values.websiteURL} />
            <GridLine name="Admin's First Name" value={values.firstName} />
            <GridLine name="Admin's Last Name" value={values.lastName} />
            <GridLine name="Admin's Phone" value={formatPhoneNumber(values.phoneNumber)} />
            <GridLine name="Admin's Email" value={values.email} />
          </Grid>

          <Box display="flex" gap={2} mt={4} justifyContent="center">
            <Button
              variant="outlined"
              color="infoButton"
              onClick={onCloseModal}
              disabled={submitting}
              sx={btnStyles}
            >
              Close
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !valid}
              loading={submitting}
              sx={btnStyles}
            >
              Save Changes
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};

const modalTextStyles: SxProp = {
  opacity: '0.8',
};

const btnStyles: SxProp = {
  width: '160px',
};

const linkStyles: SxProp = {
  color: '#42A5F5',
};

const GridLine = ({ name, value }: { name: string; value: Nullable<string> }) => {
  return (
    <Grid container spacing={2}>
      <Grid size={4}>
        <Typography variant="body1" color="text.secondary" component="p">
          {name}
        </Typography>
      </Grid>
      <Grid size={8}>
        <Typography variant="body1" component="p">
          {parseNullableValue(value)}
        </Typography>
      </Grid>
    </Grid>
  );
};
